import { useNavigate } from 'react-router-dom';
import { Row, Col, Button, List, Empty, Spin, Table, Tooltip as AntTooltip } from 'antd';
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { dashboardService } from '@/services/dashboardService';
import { taskService } from '@/services/taskService';
import { useAuthStore } from '@/store/authStore';
import { Card } from '@/components/common/PageHeader';
import { TaskStatusBadge, PriorityBadge } from '@/components/common/Badges';
import { EisenhowerMatrix } from '@/components/dashboard/EisenhowerMatrix';
import { getPronounCapitalized } from '@/utils/genderHelper';  // V1.0.48
import dayjs from 'dayjs';

function KpiCard({
  label, value, color, onClick,
}: { label: string; value: string | number; color?: string; onClick?: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff', border: '1px solid #e1e5eb', borderRadius: 8,
        padding: 16, cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.borderColor = color ?? '#6699CC';
          e.currentTarget.style.boxShadow = `0 2px 8px ${(color ?? '#6699CC')}22`;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#e1e5eb';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ fontSize: 11, color: '#7d8a98', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 30, fontWeight: 600, color: color ?? '#1a1e23', marginTop: 4, lineHeight: 1.1 }}>
        {value}
      </div>
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-user'],
    queryFn: () => dashboardService.user(),
    refetchInterval: 60 * 1000, // refresh mỗi phút
  });

  // Lấy task active của user để dựng ma trận Eisenhower
  const { data: myTasks, isLoading: tasksLoading } = useQuery({
    queryKey: ['tasks', { tab: 'assigned-to-me' }],
    queryFn: () => taskService.list({ tab: 'assigned-to-me' }),
  });

  if (isLoading || !data) {
    return <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>;
  }

  // Chart data: convert ISO date → labels T2/T3/T4... và count
  const weeklyData = data.chart7Days.map(p => ({
    day: dayjs(p.date).format('ddd'),
    completed: p.count,
  }));

  return (
    <div>
      {/* Greeting */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>
          {/* V1.0.50: Xưng hô trung tính "Bạn" - không phân biệt giới tính */}
          Xin chào, {user?.fullName ? `${getPronounCapitalized(user.gender)} ${user.fullName}` : 'bạn'}!
        </h1>
        <div style={{ fontSize: 13, color: '#7d8a98', marginTop: 4 }}>
          {dayjs().format('dddd, DD [tháng] MM [năm] YYYY')}
        </div>
      </div>

      {/* KPIs */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={12} md={6}>
          <KpiCard label="Đang thực hiện" value={data.inProgress} color="#6699CC"
            onClick={() => navigate('/tasks?tab=assigned-to-me')} />
        </Col>
        <Col xs={12} md={6}>
          <KpiCard label="Sắp đến hạn (48h)" value={data.dueSoon} color="#faad14"
            onClick={() => navigate('/tasks?filter=due-soon')} />
        </Col>
        <Col xs={12} md={6}>
          <KpiCard label="Quá hạn" value={data.overdue} color="#f5222d"
            onClick={() => navigate('/tasks?filter=overdue')} />
        </Col>
        <Col xs={12} md={6}>
          <KpiCard label="Hoàn thành tuần này" value={data.completedThisWeek} color="#52c41a" />
        </Col>
      </Row>

      {/* Ma trận Eisenhower — phân loại task theo Quan trọng × Gấp */}
      <div style={{ marginBottom: 20 }}>
        <EisenhowerMatrix
          tasks={myTasks?.items ?? []}
          loading={tasksLoading}
          title="📊 Ma trận Eisenhower — Task của tôi"
        />
      </div>

      <Row gutter={[16, 16]}>
        {/* Weekly chart */}
        <Col xs={24} md={12}>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
              Hoạt động 7 ngày qua
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={weeklyData}>
                <XAxis dataKey="day" fontSize={11} />
                <Tooltip />
                <Bar dataKey="completed" fill="#6699CC" name="Hoàn thành" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Today tasks */}
        <Col xs={24} md={12}>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
              <span>Việc sắp đến hạn (7 ngày)</span>
              <Button type="link" size="small" onClick={() => navigate('/tasks')}>Xem tất cả</Button>
            </div>
            {data.todayTasks.length === 0 ? (
              <Empty description="Không có task sắp đến hạn" style={{ padding: 20 }} />
            ) : (
              <List
                size="small"
                dataSource={data.todayTasks}
                renderItem={(t) => (
                  <List.Item style={{ cursor: 'pointer', padding: '8px 0' }}
                    onClick={() => navigate(`/tasks/${t.id}`)}>
                    <div style={{ width: '100%', display: 'flex', gap: 8, alignItems: 'center' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.title}
                        </div>
                        <div style={{ fontSize: 11, color: '#7d8a98' }}>
                          {t.code} · Hạn {dayjs(t.dueDate).format('DD/MM')}
                        </div>
                      </div>
                      <PriorityBadge priority={t.priority as any} />
                      <TaskStatusBadge status={t.status as any} />
                    </div>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        {/* Pending signatures - V1.0.60: Bảng có cột Mô tả ký + Hạn ký rõ ràng */}
        <Col xs={24}>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
              <span>Đang chờ tôi ký ({data.pendingSignatures})</span>
              <Button type="link" size="small" onClick={() => navigate('/signing')}>Xem tất cả</Button>
            </div>
            {data.pendingSigList.length === 0 ? (
              <Empty description="Không có yêu cầu ký nào" style={{ padding: 20 }} />
            ) : (
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={data.pendingSigList}
                onRow={(s) => ({
                  onClick: () => navigate(`/signing/${s.id}`),
                  style: { cursor: 'pointer' },
                })}
                columns={[
                  {
                    title: 'Tài liệu',
                    dataIndex: 'documentName',
                    key: 'documentName',
                    render: (_: string, s: any) => (
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: '#1a1e23' }}>
                          {s.documentName}
                        </div>
                        <div style={{ fontSize: 11, color: '#7d8a98', marginTop: 2 }}>
                          {s.requesterName && <>Từ <b>{s.requesterName}</b> · </>}
                          Gửi {dayjs(s.createdAt).format('DD/MM/YYYY HH:mm')}
                        </div>
                      </div>
                    ),
                  },
                  {
                    title: 'Mô tả ký',
                    dataIndex: 'taskDescription',
                    key: 'taskDescription',
                    width: 280,
                    render: (note: string | undefined) => {
                      if (!note) return <span style={{ color: '#bdc4cd', fontStyle: 'italic', fontSize: 12 }}>—</span>;
                      const short = note.length > 80 ? note.substring(0, 80) + '…' : note;
                      return (
                        <AntTooltip title={note.length > 80 ? note : undefined}>
                          <div style={{
                            fontSize: 12, color: '#1a1e23',
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                            lineHeight: 1.5,
                          }}>
                            {short}
                          </div>
                        </AntTooltip>
                      );
                    },
                  },
                  {
                    title: 'Hạn ký',
                    dataIndex: 'dueDate',
                    key: 'dueDate',
                    width: 160,
                    align: 'center',
                    render: (dueDate: string | undefined) => {
                      if (!dueDate) {
                        return <span style={{ color: '#bdc4cd', fontStyle: 'italic', fontSize: 12 }}>Không hạn</span>;
                      }
                      const due = dayjs(dueDate);
                      const now = dayjs();
                      const daysLeft = due.startOf('day').diff(now.startOf('day'), 'day');
                      let dueColor = '#1976D2';
                      let dueBg = '#e6f4ff';
                      let dueLabel = `Còn ${daysLeft} ngày`;
                      if (daysLeft < 0) {
                        dueColor = '#cf1322'; dueBg = '#fff1f0';
                        dueLabel = `⚠ Quá hạn ${Math.abs(daysLeft)} ngày`;
                      } else if (daysLeft === 0) {
                        dueColor = '#fa8c16'; dueBg = '#fff7e6';
                        dueLabel = '⏰ Hết hạn hôm nay';
                      } else if (daysLeft <= 2) {
                        dueColor = '#fa8c16'; dueBg = '#fff7e6';
                        dueLabel = `⏰ Còn ${daysLeft} ngày`;
                      }
                      return (
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: dueColor }}>
                            {due.format('DD/MM/YYYY')}
                          </div>
                          <span style={{
                            display: 'inline-block', marginTop: 4,
                            fontSize: 10, fontWeight: 600,
                            padding: '1px 6px', borderRadius: 4,
                            color: dueColor, background: dueBg,
                            border: `1px solid ${dueColor}33`,
                          }}>
                            {dueLabel}
                          </span>
                        </div>
                      );
                    },
                  },
                  {
                    title: '',
                    key: 'action',
                    width: 80,
                    align: 'center',
                    render: (_: any, s: any) => (
                      <Button type="primary" size="small"
                        onClick={(e) => { e.stopPropagation(); navigate(`/signing/${s.id}`); }}>
                        Ký
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
